import { useState } from 'react'
import { Eye, EyeOff, User, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog'
import { useAuthStore } from '@/stores/auth-store'

interface LoginModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  actionText?: string // e.g., "om dit recept te liken", "om een reactie te plaatsen"
}

export function LoginModal({ 
  open, 
  onOpenChange, 
  onSuccess, 
  actionText = "om deze actie uit te voeren" 
}: LoginModalProps) {
  const { login, register, loading, error, pendingConfirmationEmail, clearPendingConfirmation } = useAuthStore()
  const [isRegistering, setIsRegistering] = useState(false)
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isRegistering) {
      if (formData.password !== formData.confirmPassword) {
        return // Password validation should be handled by form validation
      }
      
      const success = await register(formData.email, formData.password, formData.name)
      if (success) {
        const { pendingConfirmationEmail: nextPendingConfirmationEmail, isAuthenticated } = useAuthStore.getState()
        if (nextPendingConfirmationEmail || !isAuthenticated) {
          setRegistrationSubmitted(true)
          return
        }

        onOpenChange(false)
        onSuccess?.()
      }
    } else {
      const success = await login(formData.email, formData.password)
      if (success) {
        onOpenChange(false)
        onSuccess?.()
      }
    }
  }

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    })
    setIsRegistering(false)
    setRegistrationSubmitted(false)
    setShowPassword(false)
  }

  const handleClose = () => {
    resetForm()
    clearPendingConfirmation()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isRegistering ? 'Account aanmaken' : 'Inloggen'}
          </DialogTitle>
          <DialogDescription>
            Je moet ingelogd zijn {actionText}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {registrationSubmitted && pendingConfirmationEmail ? (
            <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
              Bevestig eerst je e-mailadres via de mail naar <span className="font-medium">{pendingConfirmationEmail}</span>. Daarna kun je hier direct verder.
            </div>
          ) : null}

          {isRegistering && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Naam
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder="Je volledige naam"
                required={isRegistering}
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-mailadres
            </label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              placeholder="je@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Wachtwoord
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                placeholder="Je wachtwoord"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {isRegistering && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Wachtwoord bevestigen
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                placeholder="Bevestig je wachtwoord"
                required={isRegistering}
                minLength={6}
              />
              {isRegistering && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-600 text-xs mt-1">Wachtwoorden komen niet overeen</p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <DialogFooter>
            <DialogClose onClick={handleClose}>
              Annuleren
            </DialogClose>
            <Button
              type="submit"
              disabled={loading || registrationSubmitted || (isRegistering && formData.password !== formData.confirmPassword)}
              className="flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              {isRegistering ? 'Account aanmaken' : 'Inloggen'}
            </Button>
          </DialogFooter>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              setIsRegistering(!isRegistering)
              setRegistrationSubmitted(false)
              clearPendingConfirmation()
            }}
            className="text-sm text-orange-600 hover:text-orange-700 underline"
            disabled={loading}
          >
            {isRegistering 
              ? 'Heb je al een account? Inloggen' 
              : 'Nog geen account? Registreren'
            }
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
